import { Box, Popover, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { DateSelect } from 'components/core/date-select'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type DateRangeFilterProps = {
    startDate: Timestamp | undefined
    endDate: Timestamp | undefined
    onChange: (range: { startDate?: Timestamp, endDate?: Timestamp }, loading?: boolean) => void
}

export const DateRangeFilter: FC<DateRangeFilterProps> = ({ startDate, endDate, onChange }) => {
    const intl = useIntl()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const open = Boolean(anchorEl)
    const [validationError, setValidationError] = useState<string | undefined>(undefined)

    // Create internal state to track dates for the UI
    const [internalStartDate, setInternalStartDate] = useState<Date | null>(startDate ? getDateFromTimestamp(startDate) : null)
    const [internalEndDate, setInternalEndDate] = useState<Date | null>(endDate ? getDateFromTimestamp(endDate) : null)

    // Update internal state when props change (including when they're cleared)
    useEffect(() => {
        setInternalStartDate(startDate ? getDateFromTimestamp(startDate) : null)
    }, [startDate])

    useEffect(() => {
        setInternalEndDate(endDate ? getDateFromTimestamp(endDate) : null)
    }, [endDate])

    // Clear error when dates change via props
    useEffect(() => {
        setValidationError(undefined)
    }, [startDate, endDate])

    // Helper function to convert Timestamp to Date
    function getDateFromTimestamp(timestamp: Timestamp | undefined): Date | null {
        if (!timestamp) return null
        try {
            return Timestamp.toDate(timestamp)
        } catch (err) {
            return null
        }
    }

    const validateDateRange = (start?: Date | null, end?: Date | null): boolean => {
        if (start && end && start > end) {
            setValidationError(intl.formatMessage({
                id: 'component_daterage_end_min_error',
                defaultMessage: 'End date cannot be before start date',
            }))
            return false
        }
        setValidationError(undefined)
        return true
    }

    const handleStartDateChange = (date: Date | null): void => {
        setInternalStartDate(date)
        if (date && internalEndDate && !validateDateRange(date, internalEndDate)) {
            return
        }
        if (date) {
            const timestamp = Timestamp.fromDate(date)
            onChange({ startDate: timestamp, endDate }, false)
        } else {
            onChange({ startDate: undefined, endDate }, false)
        }
    }

    const handleEndDateChange = (date: Date | null): void => {
        setInternalEndDate(date)
        if (internalStartDate && date && !validateDateRange(internalStartDate, date)) {
            return
        }
        if (date) {
            const timestamp = Timestamp.fromDate(date)
            onChange({ startDate, endDate: timestamp }, false)
        } else {
            onChange({ startDate, endDate: undefined }, false)
        }
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (): void => {
        setAnchorEl(null)
    }

    const formatDate = (date: Date | null): string => {
        if (!date) return ''
        return date.toLocaleDateString()
    }

    const getButtonLabel = (): string => {
        if (internalStartDate && internalEndDate) {
            return `${formatDate(internalStartDate)} - ${formatDate(internalEndDate)}`
        }
        if (internalStartDate) {
            return `${formatDate(internalStartDate)} →`
        }
        if (internalEndDate) {
            return `← ${formatDate(internalEndDate)}`
        }
        return intl.formatMessage({ id: 'task_user_filter_created_between', defaultMessage: 'Created between' })
    }

    return (
        <>
            <C1Button
                variant="outlined"
                color={internalStartDate || internalEndDate ? 'primary' : 'info'}
                onClick={handleClick}
                sx={{ borderColor: 'divider' }}
            >
                {getButtonLabel()}
            </C1Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="start">
                        <Stack direction="column" spacing={1} alignItems="start">
                            <Typography variant="subtitle2" gutterBottom>
                                <FormattedMessage id="task_user_filter_created_after" defaultMessage="Created after" />
                            </Typography>
                            <DateSelect
                                fullWidth
                                value={internalStartDate}
                                onChange={handleStartDateChange}
                                disableFuture
                                error={!!validationError}
                            />
                        </Stack>
                        <Stack direction="column" spacing={1} alignItems="start">
                            <Typography variant="subtitle2" gutterBottom>
                                <FormattedMessage id="task_user_filter_created_before" defaultMessage="Created before" />
                            </Typography>
                            <DateSelect
                                fullWidth
                                value={internalEndDate}
                                onChange={handleEndDateChange}
                                disableFuture
                                error={!!validationError}
                                helperText={validationError}
                            />
                        </Stack>
                    </Stack>
                </Box>
            </Popover>
        </>
    )
}
